import { IconButton } from '@atlaskit/button/new';
import EditIcon from '@atlaskit/icon/core/edit';
import { MenuGroup } from '@atlaskit/menu';
import { type PopupProps, type TriggerProps } from '@atlaskit/popup';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import Popup from '@databases/atlaskit/Popup';
import { messages } from '@databases/intl/messages';
import { useComponent, useStateWithOverride } from '@databases/react';
import { type SelectedValue } from '@databases/redux/cellSelections/slice';
import { type DatabaseValue } from '@databases/types/database';
import DatabaseValueBulkEdit from '@databases/value/DatabaseValueBulkEdit';
import { isEqual } from 'lodash-es';
import React, {
  type DetailedHTMLProps,
  type FunctionComponent,
  type HTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

const ContentContainer = styled.div`
  padding: ${token('space.050', '4px')};
`;

type TriggerPropsWithHtmlProps = TriggerProps &
  DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export type BulkEditValuesProps = {
  contentId: string;
  initialValue?: DatabaseValue;
  isOpen?: boolean;
  offset?: [number, number];
  placement?: PopupProps['placement'];
  removeSelectedContent: () => void;
  selectedValues: SelectedValue[];
  Trigger?: (props: TriggerPropsWithHtmlProps) => React.ReactElement;
};

const BulkEditValues: FunctionComponent<BulkEditValuesProps> = ({
  contentId,
  initialValue,
  isOpen,
  offset,
  placement,
  removeSelectedContent,
  selectedValues,
  Trigger,
}) => {
  const isPopoverEditEnabled = isFeatureFlagEnabled('databases-frontend-ci2-m1');
  const [value, setValue] = useState<DatabaseValue | undefined>(initialValue);
  const prevValue = useRef(value);
  const handleBulkEditChange = useCallback(
    (value: DatabaseValue) => {
      if (prevValue.current === undefined) {
        removeSelectedContent();
      }

      prevValue.current = value;

      setValue(value);
    },
    [removeSelectedContent]
  );

  const [isBulkEditOpen, setIsBulkEditOpen] = useStateWithOverride(false, isOpen);
  const handleToggleIsOpen = useCallback(() => {
    if (!isBulkEditOpen) {
      setValue(undefined);
    }
    setIsBulkEditOpen(!isBulkEditOpen);
  }, [isBulkEditOpen, setIsBulkEditOpen]);

  const prevSelectedValues = useRef(selectedValues);

  useEffect(() => {
    if (isBulkEditOpen && !isEqual(selectedValues, prevSelectedValues.current)) {
      setValue(undefined);
    }

    prevSelectedValues.current = selectedValues;
  }, [isBulkEditOpen, selectedValues]);

  const { formatMessage } = useIntl();
  const trigger: PopupProps['trigger'] = useComponent(
    ({ ref, ...props }: TriggerProps) => {
      return Trigger && typeof trigger === 'function' ? (
        Trigger({
          ref,
          ...props,
        })
      ) : (
        <Tooltip content={formatMessage(messages['bulk-edit-values.tooltip'])}>
          <IconButton
            icon={(iconProps) => <EditIcon {...iconProps} size="medium" />}
            label={formatMessage(messages['bulk-edit-values.label'])}
            spacing="compact"
            appearance="subtle"
            isSelected={isBulkEditOpen}
            onClick={(e: React.MouseEvent) => {
              handleToggleIsOpen();
              e.stopPropagation();
              e.preventDefault();
            }}
            ref={ref}
            {...props}
          />
        </Tooltip>
      );
    },
    [Trigger, formatMessage, handleToggleIsOpen, isBulkEditOpen]
  );

  return (
    <Popup
      id="bulk-edit-values"
      autoFocus={false}
      isOpen={isBulkEditOpen}
      onClose={handleToggleIsOpen}
      contentContainer={ContentContainer}
      trigger={trigger}
      placement={placement}
      offset={offset}
      content={({ setInitialFocusRef }) => (
        <MenuGroup
          minWidth="200px"
          maxHeight="435px"
          onClick={(e) => e.stopPropagation()}
        >
          <DatabaseValueBulkEdit
            contentId={contentId}
            value={isPopoverEditEnabled ? initialValue : value}
            onBulkEditChange={handleBulkEditChange}
            ref={setInitialFocusRef}
          />
        </MenuGroup>
      )}
    />
  );
};

export default BulkEditValues;
